import * as PropTypes from 'prop-types';
import React from 'react';

import renderExpandedListing from 'nav/expanded/RenderExpandedListing';
import BenchlingProfiler from 'performance/BenchlingProfiler';
import {composeSidepanelSearchResultsRenderedEvent} from 'performance/processSearchEvents';
import {ListableItem, SearchResults, Sort} from 'search/SearchConstants';
import {trackSelectedSearchResult} from 'search/searchTracking';
import ExpandedScrollableTable from 'ui-library/scrollable/ExpandedScrollableTable';
import {ScrollableTableRow, TABLE_KEYS} from 'ui-library/scrollable/ScrollableTableConstants';
import {cycleColumnHeaderSort} from 'ui-library/scrollable/ScrollableTableUtil';

import * as WorkflowActions from '../WorkflowActions';
import {
  WORKFLOW_DASHBOARD_DETAILS_SHAPE,
  WORKFLOW_SUMMARY_SHAPE,
  WorkflowDashboardDetails,
  WorkflowSummary,
} from '../WorkflowConstants';

interface WorkflowTableProps {
  workflows: Array<WorkflowSummary>;
  workflowIdToDashboardDetails: {[workflowId: string]: WorkflowDashboardDetails};
  emptyMessage: string;
  searchResults: SearchResults | null;
  sorts: Array<Sort>;
  onSetColumnHeaderSort: (sort: Sort) => void;
}

export default class WorkflowTable extends React.Component<WorkflowTableProps> {
  static propTypes = {
    workflows: PropTypes.arrayOf(WORKFLOW_SUMMARY_SHAPE).isRequired,
    workflowIdToDashboardDetails: PropTypes.objectOf(WORKFLOW_DASHBOARD_DETAILS_SHAPE).isRequired,
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleOpenWorkflow = (row: ScrollableTableRow) => {
    trackSelectedSearchResult(row.id, this.props.searchResults);
    WorkflowActions.openWorkflowDetailsScreen(row.id);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSetSort = (sortKey: string) => {
    const newSort = cycleColumnHeaderSort(this.props.sorts, sortKey);
    this.props.onSetColumnHeaderSort(newSort);
  };

  render(): JSX.Element {
    // Since we aren't passing groups, we can ignore the group related return values safely.
    const {columns, rowDataByItemId, iconsByItemId} = renderExpandedListing(
      // This cast is because we didn't add permissions, which are required in ListableItem.
      this.props.workflows as Array<ListableItem>,
      this.props.sorts,
      {
        workflowIdToDashboardDetails: this.props.workflowIdToDashboardDetails,
      }
    );

    return (
      <BenchlingProfiler {...composeSidepanelSearchResultsRenderedEvent()}>
        <ExpandedScrollableTable
          canFilterColumns={true}
          className="u-flex-grow u-flex-full-height"
          matchParentHeight={true}
          columns={columns}
          onRowClick={this._handleOpenWorkflow}
          emptyTableText={this.props.emptyMessage}
          rows={this.props.workflows.map((workflow) => ({
            id: workflow.id,
            data: rowDataByItemId[workflow.id],
            icon: iconsByItemId[workflow.id],
          }))}
          isCompact={false}
          hasLineBelowHeader={false}
          tableKey={TABLE_KEYS.EXPANDED_WORKFLOW_TABLE}
          onChangeSort={this._handleSetSort}
        />
      </BenchlingProfiler>
    );
  }
}
